import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import '../App.css';

interface Invitation {
  id: string;
  teamId: string;
  token: string;
  role: string;
  position?: string;
  expiresAt: string;
  team: { id: string; name: string; teamNumber: string } | null;
  creator: { id: string; firstName: string; lastName: string } | null;
  isAlreadyMember: boolean;
}

const AcceptInvitation: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) {
      setError('Please log in to accept this invitation');
      setLoading(false);
      return;
    }

    fetchInvitation();
  }, [token, user]);

  const fetchInvitation = async () => {
    try {
      const response = await api.get(`/invitations/${token}`);
      setInvitation(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!invitation) return;

    setAccepting(true);
    try {
      const response = await api.post(`/invitations/${token}/accept`);
      alert(`Successfully joined ${response.data.team.name}!`);
      navigate(`/teams/${response.data.team.id}`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ maxWidth: '600px', marginTop: '100px' }}>
        <div className="card">
          <p>Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container" style={{ maxWidth: '600px', marginTop: '100px' }}>
        <div className="card">
          <h2>Invitation Error</h2>
          <div className="error">{error}</div>
          {!user && (
            <div style={{ marginTop: '20px' }}>
              <a href="/login" className="btn btn-primary">Login</a>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!invitation) {
    return (
      <div className="container" style={{ maxWidth: '600px', marginTop: '100px' }}>
        <div className="card">
          <h2>Invitation Not Found</h2>
          <p>The invitation link is invalid or has expired.</p>
        </div>
      </div>
    );
  }

  if (invitation.isAlreadyMember) {
    return (
      <div className="container" style={{ maxWidth: '600px', marginTop: '100px' }}>
        <div className="card">
          <h2>Already a Member</h2>
          <p>You are already a member of {invitation.team?.name}.</p>
          <button className="btn btn-primary" onClick={() => navigate(`/teams/${invitation.teamId}`)}>
            View Team
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ maxWidth: '600px', marginTop: '100px' }}>
      <div className="card">
        <h1>Team Invitation</h1>
        <div style={{ marginTop: '30px' }}>
          <p><strong>Team:</strong> {invitation.team?.name} ({invitation.team?.teamNumber})</p>
          {invitation.creator && (
            <p><strong>Invited by:</strong> {invitation.creator.firstName} {invitation.creator.lastName}</p>
          )}
          <p><strong>Role:</strong> <span className="badge badge-info">{invitation.role}</span></p>
          {invitation.position && (
            <p><strong>Position:</strong> {invitation.position}</p>
          )}
          <p><strong>Expires:</strong> {new Date(invitation.expiresAt).toLocaleDateString()}</p>
        </div>

        <div style={{ marginTop: '30px', padding: '15px', background: '#E8F5E9', borderRadius: '8px' }}>
          <p style={{ margin: 0 }}>
            <strong>Welcome, {user?.firstName}!</strong> Click the button below to join this team.
          </p>
        </div>

        <div style={{ marginTop: '30px', display: 'flex', gap: '10px' }}>
          <button
            className="btn btn-primary"
            onClick={handleAccept}
            disabled={accepting}
          >
            {accepting ? 'Joining...' : 'Accept Invitation'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/dashboard')}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitation;

